import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Page, Button, Spinner, TableList, FilterPickerGroup, PageContentHeader } from 'seagull';
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { formatLongMonthUSDate } from "../../../utils";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { activeServicesReportColumnDefs, activeServicesReportHeaderColumnDefs, activeServicesReportHeaderColumnDefsForExport, filterLabels, getDefaultSort, makeCsv, makeFiltersConfig } from "./ActiveServices.utils";
import { FilterType } from "./types";
import { ActiveServicesReportOptionalColumns } from "./types";
import TableFilters from "../components/TableFilters/TableFilters";
import { VisibleColumnsPicker } from "../components/VisibleColumnsPicker/VisibleColumnsPicker";
import { ActiveServiceRow } from "./ActiveServiceRow";
const title = 'Active Services Report';
export const ActiveServicesReport = () => {
  const businessId = useStore(selectors.user.businessId);
  const businessName = useStore(selectors.user.businessName);
  const {
    api
  } = useStoreActions();
  const {
    downloadCsv
  } = useIoContext();
  const history = useHistory();
  const [visibleColumns, setVisibleColumns] = useState({
    [ActiveServicesReportOptionalColumns.Trigger]: false,
    [ActiveServicesReportOptionalColumns.Pricing]: false,
    [ActiveServicesReportOptionalColumns.Occurrence]: true,
    [ActiveServicesReportOptionalColumns.Rate]: true,
    [ActiveServicesReportOptionalColumns.LastBilled]: false,
    [ActiveServicesReportOptionalColumns.Assignee]: false,
    [ActiveServicesReportOptionalColumns.AutomaticServiceState]: false,
    [ActiveServicesReportOptionalColumns.Status]: true
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [availableAgreements, setAvailableAgreements] = useState([]);
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const headerItems = useMemo(() => activeServicesReportHeaderColumnDefs(visibleColumns), [visibleColumns]);
  const sortIds = useMemo(() => headerItems.map(item => item.sortId), [headerItems]);
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const sortStringified = sort && JSON.stringify(sort);
  const filtersConfig = useMemo(() => makeFiltersConfig(availableServices, availableCustomers, availableAgreements, availableAssignees), [availableServices, availableCustomers, availableAgreements, availableAssignees]);
  function updateVisibleColumns(columns) {
    setVisibleColumns(columns);
    if (sort) {
      const currentSortId = sortIds[sort.colIndex];
      const visibleKeys = Object.keys(columns).filter(key => !!columns[key]);
      const visibleSortIds = headerItems.filter(item => !item.optionalColumn || visibleKeys.includes(item.optionalColumn)).map(item => item.sortId);
      if (!visibleSortIds.includes(currentSortId)) {
        changeSort(getDefaultSort(visibleSortIds));
      }
    }
  }
  useEffect(() => {
    document.title = title;
    api.reports.activeServicesReportServices().then(setAvailableServices);
    api.reports.activeServicesReportCustomers().then(setAvailableCustomers);
    api.reports.activeServicesReportAgreements().then(setAvailableAgreements);
    api.reports.activeServicesReportAssignees().then(setAvailableAssignees);
  }, []);
  const fetchActiveServicesReport = () => {
    var _selectedFilters$Filt, _selectedFilters$Filt2;
    if (businessId == null) return;
    setIsRefreshing(true);
    api.reports.activeServicesReport({
      businessId,
      customers: selectedFilters[FilterType.CustomerName] || [],
      services: selectedFilters[FilterType.ServiceName] || [],
      agreements: selectedFilters[FilterType.AgreementName] || [],
      assignees: selectedFilters[FilterType.Assignee] || [],
      params: {
        sort: sort ? [sortIds[sort.colIndex], sort.sort] : undefined,
        occurrence: selectedFilters[FilterType.Occurrence],
        trigger: selectedFilters[FilterType.Trigger],
        pricing: selectedFilters[FilterType.Pricing],
        automaticServiceState: selectedFilters[FilterType.AutomaticServiceState],
        status: selectedFilters[FilterType.Status],
        lastBilledSince: (_selectedFilters$Filt = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.from,
        lastBilledUntil: (_selectedFilters$Filt2 = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.to
      }
    }).then(data => {
      setReportData(data);
      setIsRefreshing(false);
    });
  };
  useEffect(() => {
    fetchActiveServicesReport();
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [selectedFilters[FilterType.CustomerName], selectedFilters[FilterType.ServiceName], selectedFilters[FilterType.AgreementName], selectedFilters[FilterType.Assignee], selectedFilters[FilterType.Occurrence], selectedFilters[FilterType.Trigger], selectedFilters[FilterType.Pricing], selectedFilters[FilterType.AutomaticServiceState], selectedFilters[FilterType.Status], selectedFilters[FilterType.LastBilled], sortStringified]);
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "active-services-report",
    header: "Active Services Report",
    variant: "containedHeight",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      skin: "primary",
      "data-testid": "export-report-btn",
      onClick: () => downloadCsv(makeCsv(activeServicesReportHeaderColumnDefsForExport(visibleColumns).map(_ref => {
        let {
          value
        } = _ref;
        return value;
      }), visibleColumns, reportData), "".concat(businessName, "_active_services_report_").concat(formatLongMonthUSDate(new Date()), ".csv"))
    }, "Export"))
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: activeServicesReportColumnDefs(visibleColumns),
    numRows: reportData.length,
    sort: sort,
    onSortChange: changeSort
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    flexWrap: true
  }, /*#__PURE__*/React.createElement(VisibleColumnsPicker, {
    testId: "active-services-report",
    visibleColumns: visibleColumns,
    onApply: updateVisibleColumns
  }), /*#__PURE__*/React.createElement(TableFilters, {
    testIdPrefix: "active-services-report",
    filterLabels: filterLabels,
    filterConfig: filtersConfig,
    selectedFilters: selectedFilters,
    setSelectedFilters: setSelectedFilters,
    onRefresh: fetchActiveServicesReport
  })))), /*#__PURE__*/React.createElement(TableList.Structure, {
    header: /*#__PURE__*/React.createElement(TableList.Header, {
      stickyToTop: true,
      items: headerItems,
      "data-testid": "active-services-report-table-header"
    }),
    content: isRefreshing ? /*#__PURE__*/React.createElement("div", {
      "data-testid": "active-services-table-loading"
    }, /*#__PURE__*/React.createElement(Spinner, {
      centeredInBox: true
    })) : reportData.length ? reportData.map((reportRow, index) => /*#__PURE__*/React.createElement(ActiveServiceRow, {
      key: "".concat(reportRow.customerName, "-").concat(reportRow.serviceName, "-").concat(index),
      index: index,
      data: reportRow,
      visibleColumns: visibleColumns
    })) : null
  })));
};